// Core data model interfaces for the hospital portal
// Based on Prisma schema to ensure backend API alignment

// Common types and enums
export enum UserType {
  NURSE = 'nurse',
  HOSPITAL_ADMIN = 'hospital_admin',
  DEPARTMENT_ADMIN = 'department_admin',
  SYSTEM_ADMIN = 'system_admin'
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  SUSPENDED = 'suspended'
}

export enum JobType {
  CNA = 'CNA',
  LPN = 'LPN',
  RN = 'RN',
  NP = 'NP',
  SITTER = 'SITTER',
  Other = 'Other'
}

export enum ShiftStatus {
  OPEN = 'open',
  BOOKED = 'booked',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum ApplicationStatus {
  SUBMITTED = 'submitted',
  UNDER_REVIEW = 'under_review',
  INTERVIEW_SCHEDULED = 'interview_scheduled',
  INTERVIEW_COMPLETED = 'interview_completed',
  REFERENCE_CHECK = 'reference_check',
  OFFER_PENDING = 'offer_pending',
  HIRED = 'hired',
  REJECTED = 'rejected'
}

export enum ShiftTimeOfDay {
  AM = 'AM',
  PM = 'PM',
  NOC = 'NOC',
  CUSTOM = 'CUSTOM'
}

export enum ShiftPattern {
  ALL_DAYS = 'all_days',
  WEEKDAYS = 'weekdays',
  WEEKENDS = 'weekends',
  CUSTOM = 'custom'
}

// ShiftTimeOfDay와 ShiftTimeType은 실제로 동일한 의미로 사용됨
// 백엔드와의 일관성을 위해 ShiftTimeType alias 추가
export type ShiftTimeType = ShiftTimeOfDay;

// Core entity interfaces
export interface User {
  id: string;
  email: string;
  name: string;
  phone: string;
  userType: UserType;
  status: UserStatus;
  emailVerified: boolean;
  phoneVerified: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Hospital {
  id: string;
  name: string;
  email?: string;
  address?: string;
  city: string;
  state: string;
  postalCode: string;
  latitude: number;
  longitude: number;
  contactPhone: string;
  contactEmail: string;
  adminId?: string;
  rating: number;
  reviewCount: number;
  phoneNumber?: string;
  description?: string;
  websiteUrl?: string;
  logoUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Department {
  id: string;
  hospitalId: string;
  name: string;
  description?: string;
  adminId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Shift {
  id: string;
  hospitalId: string;
  departmentId?: string;
  jobType: JobType;
  startDate: string; // ISO format
  endDate: string; // ISO format
  startTime: string; // ISO format with time
  endTime: string; // ISO format with time
  hourlyRate: number;
  totalPay: number;
  status: ShiftStatus;
  pattern: ShiftPattern;
  timeType: ShiftTimeOfDay;
  customDays?: string[];
  experienceYears: number;
  educationLevel: string;
  licenseRequired: boolean;
  jobDescription: string;
  additionalNotes?: string;
  createdById?: string;
  createdAt: string;
  updatedAt: string;
  shiftDate?: string; // Added missing property
  timeOfDay?: ShiftTimeOfDay; // Added missing property
  
  // UI display properties (not from backend)
  hospitalName?: string;
  departmentName?: string;
  formattedStartTime?: string;
  formattedEndTime?: string;
  totalHours?: number;
  totalShifts?: number;

  // Additional properties for UI components
  title?: string; // Title for the shift
  location?: string; // Location information
  department?: string; // Department name (shorthand for departmentName)
  notes?: string; // Notes about the shift (can be used instead of additionalNotes)
  nurse?: {
    id?: string;
    name?: string;
    profileImage?: string;
    specialties?: string[];
    experienceYears?: number;
    rating?: number;
  }; // Associated nurse information
}

export interface NurseProfile {
  id: string;
  userId: string;
  licenseNumber: string;
  licenseVerified: boolean;
  experienceYears: number;
  skills?: Record<string, any>;
  preferredTimes?: Record<string, any>;
  preferredLocations?: Record<string, any>;
  rating: number;
  reviewCount: number;
  department?: string;
  hospitalName?: string;
  createdAt: string;
  updatedAt: string;
  user?: User;
}

export interface Application {
  id: string;
  shiftId: string;
  nurseId: string;
  status: ApplicationStatus;
  rejectionReason?: string;
  appliedAt: string;
  reviewedAt?: string;
  reviewedById?: string;
  interviewDate?: string;
  interviewNotes?: string;
  createdAt: string;
  nurse?: NurseProfile;
}

// Alias for Application to maintain backward compatibility
export type ShiftApplication = Application;

// DTO interfaces for API requests
export interface CreateShiftDTO {
  hospitalId: string;
  departmentId: string;
  jobType: JobType | string;
  startDate: string;  // YYYY-MM-DD 포맷
  endDate: string;    // YYYY-MM-DD 포맷
  pattern: ShiftPattern;
  timeType: ShiftTimeOfDay;
  startTime: string;  // HH:MM 포맷 (프론트엔드) 또는 ISO 문자열 (API 요청)
  endTime: string;    // HH:MM 포맷 (프론트엔드) 또는 ISO 문자열 (API 요청)
  hourlyRate: number;
  totalPay: number;
  experienceYears?: number;
  educationLevel?: string;
  licenseRequired?: boolean;
  jobDescription: string;
  additionalNotes?: string;
  customDays?: string[];  // 커스텀 날짜 패턴을 위한 배열
}

export interface UpdateShiftDTO extends Partial<CreateShiftDTO> {
  status?: ShiftStatus;
}

// UI specific interfaces
export interface ShiftFilters {
  jobType?: JobType | string;
  startDate?: string;
  endDate?: string;
  departmentId?: string;
  timeType?: ShiftTimeOfDay;
  status?: ShiftStatus;
  page?: number;
  limit?: number;
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  allDay?: boolean;
  extendedProps: {
    shift: Shift;
  };
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
}

export interface ShiftStatistics {
  totalShifts: number;
  openShifts: number;
  bookedShifts: number;
  completedShifts: number;
  cancelledShifts: number;
  averageHourlyRate: number;
}

export interface ShiftDistribution {
  byJobType: { [key: string]: number };
  byTimeType: { [key: string]: number };
  byDepartment: { [key: string]: number };
  byStatus: { [key: string]: number };
}

export interface SavedFilter {
  id: string;
  name: string;
  filters: ShiftFilters;
  createdAt: string;
  updatedAt: string;
}

// For shift calculation in CreateShiftModal
export interface ShiftCalculation {
  totalShifts: number;
  totalHours: number;
  totalPay: number;
}

// Socket.io and notification types
export * from './notification';
export * from './hospital-notification';